import { ReactNode, useEffect, useState } from 'react'

import clsx from 'clsx'

import { getRawColor } from 'shared/lib/getRawColor.ts'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types.ts'

import s from './styles.module.scss'

const animationDuration = 300 // ms
const defaultMessageDuration = 3 // seconds

export type MessageType = 'success' | 'error' | 'warning' | 'info' | 'copy'

export interface MessageProps {
  type?: MessageType
  content: ReactNode
  duration?: number
  onClose?: () => void
  className?: string
}

export const Message: React.FC<MessageProps> = ({
  type = 'info',
  content,
  duration = defaultMessageDuration,
  onClose,
  className = '',
}) => {
  const [visible, setVisible] = useState(true)

  const iconConfig = {
    success: {
      id: AllIcons.CHECK_ROUNDED,
      color: getRawColor('--bright-green'),
    },
    error: { id: AllIcons.CROSS_BOLD, color: getRawColor('--red') },
    warning: { id: AllIcons.INFO_ROUNDED, color: getRawColor('--orange') },
    info: { id: AllIcons.INFO_ROUNDED, color: getRawColor('--blue') },
    copy: { id: AllIcons.COPY_NEW, color: getRawColor('--white') },
  }

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setVisible(false)
        setTimeout(() => {
          onClose?.()
        }, animationDuration)
      }, duration * 1000)
      return () => clearTimeout(timer)
    }
  }, [duration, onClose])

  const handleClose = () => {
    setVisible(false)
    setTimeout(() => {
      onClose?.()
    }, animationDuration)
  }

  const icon = iconConfig[type] || iconConfig.info

  return (
    <div
      className={clsx(
        s.message,
        visible ? s.visible : s.hidden,
        className,
        'medium-16',
      )}
      onClick={handleClose}
    >
      <div className={s.icon}>
        <AppIcon
          id={icon.id}
          color={icon.color}
        />
      </div>
      <div className={s.content}>{content}</div>
    </div>
  )
}
